import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { 
  Filter, 
  Plus, 
  Trash2, 
  Play,
  Clock,
  AlertTriangle,
  Server,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_OPTIONS = [
  { value: 'incidents', label: 'Incidentes', icon: AlertTriangle, path: '/dashboard/incidents' },
  { value: 'incident-history', label: 'Histórico', icon: Clock, path: '/dashboard/incident-history' },
  { value: 'hosts', label: 'Hosts', icon: Server, path: '/dashboard/hosts' }
];

export function SavedFiltersPage() {
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newFilter, setNewFilter] = useState({
    name: '',
    page: 'incidents',
    filters: {}
  });
  const { getAuthHeader } = useAuthStore();
  const navigate = useNavigate();

  const fetchFilters = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/saved-filters`, { headers: getAuthHeader() });
      setFilters(response.data);
    } catch (err) {
      console.error('Error fetching filters:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  const handleCreate = async () => {
    if (!newFilter.name) {
      toast.error('Informe um nome para o filtro');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/saved-filters`, newFilter, { headers: getAuthHeader() });
      toast.success('Filtro salvo com sucesso');
      setDialogOpen(false);
      setNewFilter({ name: '', page: 'incidents', filters: {} });
      fetchFilters();
    } catch (err) {
      toast.error('Erro ao salvar filtro');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/saved-filters/${id}`, { headers: getAuthHeader() });
      toast.success('Filtro excluído');
      fetchFilters();
    } catch (err) {
      toast.error('Erro ao excluir filtro');
    }
  };

  const handleApply = (filter) => {
    const pageOption = PAGE_OPTIONS.find(p => p.value === filter.page);
    if (pageOption) {
      // Store filter in sessionStorage for the target page to pick up
      sessionStorage.setItem('appliedFilter', JSON.stringify(filter.filters));
      navigate(pageOption.path);
      toast.success(`Filtro "${filter.name}" aplicado`);
    }
  };

  const groupedFilters = PAGE_OPTIONS.map(page => ({
    ...page,
    filters: filters.filter(f => f.page === page.value)
  }));

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="saved-filters-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Filtros Salvos</h2>
          <p className="text-sm text-muted-foreground">
            Salve configurações de filtros para acesso rápido
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-filter-btn">
          <Plus className="h-4 w-4 mr-2" />
          Novo Filtro
        </Button>
      </div>

      {/* Filters by Page */}
      {filters.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Filter className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium">Nenhum filtro salvo</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Salve filtros para acessar rapidamente suas visualizações favoritas
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {groupedFilters.map(group => {
            if (group.filters.length === 0) return null;
            const Icon = group.icon;
            
            return (
              <div key={group.value}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <h3 className="font-medium">{group.label}</h3>
                  <Badge variant="secondary" className="text-xs">
                    {group.filters.length}
                  </Badge>
                </div>
                
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {group.filters.map(filter => (
                    <Card key={filter.id} className="group" data-testid={`filter-card-${filter.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-medium">{filter.name}</h4>
                            <p className="text-xs text-muted-foreground mt-1">
                              Criado em {new Date(filter.created_at).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleApply(filter)}
                              title="Aplicar filtro"
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => handleDelete(filter.id)}
                              title="Excluir filtro"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Filter preview */}
                        {Object.keys(filter.filters).length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1">
                            {Object.entries(filter.filters).slice(0, 3).map(([key, value]) => (
                              <Badge key={key} variant="outline" className="text-xs">
                                {key}: {String(value).substring(0, 15)}
                              </Badge>
                            ))}
                            {Object.keys(filter.filters).length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{Object.keys(filter.filters).length - 3}
                              </Badge>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Filter Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Salvar Filtro</DialogTitle>
            <DialogDescription>
              Dê um nome ao filtro para salvá-lo. Você pode aplicar a configuração atual de filtros da página selecionada.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome do Filtro</Label>
              <Input
                value={newFilter.name}
                onChange={(e) => setNewFilter({ ...newFilter, name: e.target.value })}
                placeholder="Ex: Problemas Críticos"
                data-testid="filter-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Página</Label>
              <Select 
                value={newFilter.page}
                onValueChange={(v) => setNewFilter({ ...newFilter, page: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <p className="text-xs text-muted-foreground bg-muted p-3 rounded">
              💡 Dica: Vá até a página desejada, configure os filtros como preferir, e depois volte aqui para salvar.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Salvar Filtro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
